import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { LegalDocumentSummary } from "@autorepair/contracts";
import { ApiError, legalApi } from "../lib/api";
import { useAuth } from "../lib/useAuth";
import { ModernButton } from "./ui-modern";

function joinTitles(titles: string[]): string {
  if (titles.length <= 1) return titles.join("");
  return `${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`;
}

/**
 * The "I have read and agree to …" checkbox, with each document title
 * linking to its page on the marketing site. Used by the post-login gate
 * below, and meant for the self-registration form once that exists.
 */
export function LegalConsentCheckbox({
  documents,
  checked,
  onChange,
}: {
  documents: Pick<LegalDocumentSummary, "id" | "title" | "url">[];
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-[14px] leading-snug text-[#071a33]">
      <input
        type="checkbox"
        className="mt-0.5 h-[18px] w-[18px] flex-none accent-[#0f6d86]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        I have read and agree to the{" "}
        {documents.map((d, i) => (
          <span key={d.id}>
            {i > 0 && (i === documents.length - 1 ? (documents.length > 2 ? ", and " : " and ") : ", ")}
            <a href={d.url} target="_blank" rel="noreferrer" className="font-semibold text-[#0f6d86] underline underline-offset-2">
              {d.title}
            </a>
          </span>
        ))}
        .
      </span>
    </label>
  );
}

/**
 * Blocking clickwrap shown over the app when the signed-in user hasn't
 * accepted the current version of a required legal document (see
 * apps/api/src/legal/). The API returns nothing pending while
 * LEGAL_GATE_ENABLED is off, so this renders nothing at all then.
 */
export function LegalAcceptanceGate() {
  const { session, logout } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["legal-status", session?.tenantId, session?.user.userId];
  const status = useQuery({ queryKey, queryFn: legalApi.status, enabled: !!session, staleTime: Infinity });
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pending = status.data?.pending ?? [];
  if (pending.length === 0) return null;

  async function accept(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await legalApi.accept({ documents: pending.map((d) => ({ id: d.id, version: d.version })) });
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      // A version mismatch means the list is stale — refetch it.
      if (err instanceof ApiError && err.status === 400) void status.refetch();
    } finally {
      setSaving(false);
      setAgreed(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="legal-gate-title">
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6" style={{ boxShadow: "0 20px 50px rgba(0,0,0,.35)" }}>
        <h2 id="legal-gate-title" className="text-[19px] font-bold text-[#071a33]">
          Please review our terms
        </h2>
        <p className="mt-2 text-[14px] text-[#5e6b7d]">
          Before you continue, please review and accept Bay2Bill's {joinTitles(pending.map((d) => d.title))}. Here's the short version:
        </p>

        <div className="mt-4 space-y-3">
          {pending.map((d) => (
            <div key={d.id} className="rounded-xl border border-[#bfe9e6] bg-[#f1fbfa] p-4">
              <div className="flex items-baseline justify-between gap-3">
                <a href={d.url} target="_blank" rel="noreferrer" className="text-[15px] font-bold text-[#071a33] underline-offset-2 hover:underline">
                  {d.title}
                </a>
                <span className="flex-none text-[12px] text-[#5e6b7d]">v{d.version}</span>
              </div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-[13.5px] text-[#243449]">
                {d.tldr.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
              <a href={d.url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[13px] font-semibold text-[#0f6d86] underline underline-offset-2">
                Read the full {d.title}
              </a>
            </div>
          ))}
        </div>

        <div className="mt-5">
          <LegalConsentCheckbox documents={pending} checked={agreed} onChange={setAgreed} />
        </div>
        {error && <p className="mt-3 text-[13.5px] text-[#b42318]">{error}</p>}

        <div className="mt-5 flex flex-wrap justify-end gap-3">
          <ModernButton variant="secondary" type="button" onClick={() => void logout()} disabled={saving}>
            Log out
          </ModernButton>
          <ModernButton type="button" onClick={() => void accept()} disabled={!agreed || saving}>
            {saving ? "Saving…" : "Agree and continue"}
          </ModernButton>
        </div>
      </div>
    </div>
  );
}
